import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles, ChevronRight, Calendar, ShieldAlert, ArrowLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "studynet_chat_session";

const CONTACT_OPTS = [
  { v: "email", l: "Email" },
  { v: "phone", l: "Phone Call" },
  { v: "whatsapp", l: "WhatsApp" },
  { v: "none", l: "No Follow-Up Required" },
];

const VISA_OPTS = [
  "Student Visa", "Visitor Visa", "Temporary Graduate Visa", "Bridging Visa",
  "Partner Visa", "Work Visa", "Permanent Resident", "Other",
];

const ENQUIRY_OPTS = [
  "Study in Australia", "Student Visa", "Graduate Visa", "Skilled Migration",
  "Employer Sponsorship", "Partner Visa", "Parent Visa", "Visitor Visa",
  "Citizenship", "Visa Refusal", "ART Appeal", "Business Sponsorship",
  "Skills Assessment", "Other",
];

const STAGE_OPTS = [
  "Just researching options", "Ready to apply", "Need professional advice",
  "Visa refused", "Need urgent assistance", "Already lodged an application",
  "Employer seeking sponsorship advice", "Student seeking course advice",
];

export default function ChatWidget() {
  const loc = useLocation();

  const [open, setOpen] = useState(false);
  // Stop pulsing once visitor has seen/opened chat (persisted via sessionStorage)
  const initialPulse = typeof window !== "undefined" && !sessionStorage.getItem("chat_seen");
  const [pulse, setPulse] = useState(initialPulse);

  // Lazy-restore session from localStorage (avoids setState-in-effect)
  const restored = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.session_id && s.messages) return s;
      }
    } catch (e) { /* noop */ }
    return null;
  })[0];

  // Onboarding state
  const [step, setStep] = useState(restored ? 99 : 0);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", contact_pref: "email",
    location: "", current_visa: "", enquiry_type: "", current_stage: "", message: "",
  });
  const [errors, setErrors] = useState({});

  // Chat state
  const [sessionId, setSessionId] = useState(restored?.session_id || null);
  const [messages, setMessages] = useState(restored?.messages || []);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  // Auto-open disabled — chat opens only on user click. Pulse animation stays to attract attention.

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, step]);

  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const stepValid = () => {
    if (step === 1) {
      const e = {};
      if (!form.full_name.trim()) e.full_name = "Required";
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
      if (!form.contact_pref) e.contact_pref = "Please choose one";
      setErrors(e);
      return Object.keys(e).length === 0;
    }
    if (step === 2) {
      const e = {};
      if (!form.location) e.location = "Required";
      if (form.location === "onshore" && !form.current_visa) e.current_visa = "Please select your current visa";
      setErrors(e); return Object.keys(e).length === 0;
    }
    if (step === 3) { const e = !form.enquiry_type ? { enquiry_type: "Required" } : {}; setErrors(e); return !e.enquiry_type; }
    if (step === 4) { const e = !form.current_stage ? { current_stage: "Required" } : {}; setErrors(e); return !e.current_stage; }
    if (step === 5) { const e = form.message.trim().length < 5 ? { message: "Please add a few words" } : {}; setErrors(e); return !e.message; }
    return true;
  };

  const next = () => { if (stepValid()) setStep((s) => s + 1); };

  const submitOnboarding = async () => {
    if (!stepValid()) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.location !== "onshore") delete payload.current_visa;
      const r = await axios.post(`${API}/chat/enquiry`, payload);
      const sid = r.data.session_id;
      const initial = [
        { role: "user", content: form.message },
        { role: "assistant", content: r.data.welcome },
      ];
      setSessionId(sid);
      setMessages(initial);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ session_id: sid, messages: initial }));
      setStep(99);
    } catch (e) {
      setErrors({ submit: e?.response?.data?.detail || "Something went wrong — please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !sessionId || sending) return;
    setSending(true);
    const userMsg = { role: "user", content: text };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput("");
    try {
      const r = await axios.post(`${API}/chat/message`, { session_id: sessionId, message: text });
      const reply = { role: "assistant", content: r.data.reply };
      const updated = [...newMsgs, reply];
      setMessages(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ session_id: sessionId, messages: updated }));
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't reach the assistant just now. Please try again — or book a consultation for direct help." }]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSessionId(null); setMessages([]); setStep(0);
    setForm({ full_name: "", email: "", phone: "", contact_pref: "email", location: "", current_visa: "", enquiry_type: "", current_stage: "", message: "" });
    setErrors({});
  };

  const progressPct = step === 99 ? 100 : Math.min(100, Math.round((step / 5) * 100));

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => { sessionStorage.setItem("chat_seen", "1"); setPulse(false); setOpen(true); }}
          aria-label="Open chat with our team"
          className="fixed bottom-5 right-5 z-50 group flex items-center gap-2 bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white pl-4 pr-5 py-3.5 shadow-[0_15px_40px_-10px_rgba(196,58,137,0.5)] hover:shadow-[0_22px_55px_-10px_rgba(196,58,137,0.7)] transition-all hover:-translate-y-0.5"
        >
          {pulse && <span aria-hidden className="absolute -inset-0.5 bg-[#C43A89]/40 animate-ping" />}
          <span className="relative inline-flex items-center justify-center w-8 h-8 bg-white/15 rounded-full">
            <MessageCircle className="h-4.5 w-4.5" />
          </span>
          <span className="relative font-display text-sm tracking-tight">Chat with us</span>
          <span className="relative bg-[#C43A89] text-white text-[9px] font-bold tracking-wider uppercase px-2 py-0.5">Live</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div data-testid="chat-panel" className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-5 sm:bottom-5 z-50 w-auto sm:w-[400px] max-w-[420px] h-[640px] max-h-[calc(100vh-24px)] bg-white shadow-[0_30px_80px_-20px_rgba(41,50,74,0.4)] border border-slate-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white p-4 relative">
            <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#C43A89] to-[#C43A89] flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-base leading-tight truncate">Chat with Our Team</div>
                  <div className="text-[10.5px] tracking-wider uppercase text-[#C43A89]">Migration · Online now</div>
                </div>
              </div>
              <button data-testid="chat-close" aria-label="Close chat" onClick={() => setOpen(false)} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            {step < 99 && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5 text-[10px] tracking-wider uppercase text-[#C43A89] font-bold">
                  <span>Step {Math.min(step, 5)} of 5</span><span>{progressPct}%</span>
                </div>
                <div className="h-1 w-full bg-white/15"><div className="h-full bg-gradient-to-r from-[#C43A89] to-[#C43A89] transition-all" style={{ width: `${progressPct}%` }} /></div>
              </div>
            )}
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[#FAF7FC] p-4 space-y-3">
            {/* Step 0 — Welcome */}
            {step === 0 && (
              <div className="space-y-3">
                <BotBubble>
                  <p className="text-[13.5px] leading-relaxed">Hi 👋</p>
                  <p className="text-[13.5px] leading-relaxed mt-2">Welcome! <strong>How can we help?</strong></p>
                  <p className="text-[13.5px] leading-relaxed mt-2">I&apos;m your virtual assistant. I can help with questions about Australian and New Zealand education, migration, employer sponsorship and visa pathways.</p>
                  <p className="text-[13px] leading-relaxed mt-3 text-slate-500">Before we begin, please share a few details so we can assist you better.</p>
                </BotBubble>
                <div className="flex justify-end">
                  <button data-testid="chat-step0-begin" onClick={() => setStep(1)} className="bg-[#29324A] hover:bg-[#1f2638] text-white text-[12px] font-bold tracking-wider uppercase px-5 py-2.5 inline-flex items-center gap-2">
                    Let&apos;s begin <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 1 — Contact */}
            {step === 1 && (
              <div className="space-y-3">
                <BotBubble><p className="text-[13.5px] leading-relaxed">Great — first, a quick contact intro. <span className="text-rose-600">*</span> required.</p></BotBubble>
                <FormCard>
                  <Field label="Full name *" err={errors.full_name}>
                    <input data-testid="chat-fld-name" value={form.full_name} onChange={(e) => u("full_name", e.target.value)} className={inp} placeholder="e.g. Asha Patel" />
                  </Field>
                  <Field label="Email *" err={errors.email}>
                    <input data-testid="chat-fld-email" type="email" value={form.email} onChange={(e) => u("email", e.target.value)} className={inp} placeholder="you@example.com" />
                  </Field>
                  <Field label="Phone (optional)">
                    <input data-testid="chat-fld-phone" value={form.phone} onChange={(e) => u("phone", e.target.value)} className={inp} placeholder="+61 400 000 000" />
                  </Field>
                  <Field label="How would you like us to follow up?" err={errors.contact_pref}>
                    <Pills opts={CONTACT_OPTS.map((c) => ({ v: c.v, l: c.l }))} value={form.contact_pref} onChange={(v) => u("contact_pref", v)} testIdPrefix="chat-pref" />
                  </Field>
                </FormCard>
              </div>
            )}

            {/* Step 2 — Location */}
            {step === 2 && (
              <div className="space-y-3">
                <BotBubble><p className="text-[13.5px] leading-relaxed">Where are you currently located?</p></BotBubble>
                <FormCard>
                  <Field label="Location *" err={errors.location}>
                    <Pills opts={[{ v: "onshore", l: "In Australia (Onshore)" }, { v: "offshore", l: "Outside Australia (Offshore)" }]} value={form.location} onChange={(v) => { u("location", v); if (v !== "onshore") u("current_visa", ""); }} testIdPrefix="chat-loc" />
                  </Field>
                  {form.location === "onshore" && (
                    <Field label="Which visa do you currently hold? *" err={errors.current_visa}>
                      <select data-testid="chat-fld-currentvisa" value={form.current_visa} onChange={(e) => u("current_visa", e.target.value)} className={inp}>
                        <option value="">Select visa</option>
                        {VISA_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  )}
                </FormCard>
              </div>
            )}

            {/* Step 3 — Enquiry type */}
            {step === 3 && (
              <div className="space-y-3">
                <BotBubble><p className="text-[13.5px] leading-relaxed">What would you like help with today?</p></BotBubble>
                <FormCard>
                  <Field label="Choose an enquiry type *" err={errors.enquiry_type}>
                    <Pills wrap opts={ENQUIRY_OPTS.map((o) => ({ v: o, l: o }))} value={form.enquiry_type} onChange={(v) => u("enquiry_type", v)} testIdPrefix="chat-enq" />
                  </Field>
                </FormCard>
              </div>
            )}

            {/* Step 4 — Stage */}
            {step === 4 && (
              <div className="space-y-3">
                <BotBubble><p className="text-[13.5px] leading-relaxed">Which best describes your current situation?</p></BotBubble>
                <FormCard>
                  <Field label="Your current stage *" err={errors.current_stage}>
                    <Pills wrap opts={STAGE_OPTS.map((o) => ({ v: o, l: o }))} value={form.current_stage} onChange={(v) => u("current_stage", v)} testIdPrefix="chat-stage" />
                  </Field>
                </FormCard>
              </div>
            )}

            {/* Step 5 — Message */}
            {step === 5 && (
              <div className="space-y-3">
                <BotBubble><p className="text-[13.5px] leading-relaxed">Last step — please tell us a little more about your situation.</p></BotBubble>
                <FormCard>
                  <Field label="Your message *" err={errors.message}>
                    <textarea data-testid="chat-fld-message" value={form.message} onChange={(e) => u("message", e.target.value)} rows={4} className={inp + " resize-none"} placeholder="e.g. I am currently living in Bangladesh and would like to know if I am eligible for a Student Visa." />
                  </Field>
                  {errors.submit && <p className="text-[12px] text-rose-600">{errors.submit}</p>}
                </FormCard>
              </div>
            )}

            {/* Chat mode */}
            {step === 99 && (
              <div className="space-y-3">
                {messages.map((m, i) => (
                  m.role === "user" ? (
                    <div key={i} className="flex justify-end">
                      <div className="max-w-[78%] bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white text-[13px] leading-relaxed px-3.5 py-2.5 shadow-sm">{m.content}</div>
                    </div>
                  ) : (
                    <BotBubble key={i}><div className="text-[13.5px] leading-relaxed whitespace-pre-line">{m.content}</div></BotBubble>
                  )
                ))}
                {sending && (
                  <BotBubble><div className="flex items-center gap-2 text-[12px] text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking...</div></BotBubble>
                )}
                {messages.length >= 4 && messages.length % 4 === 0 && (
                  <div className="bg-white border border-[#C43A89]/30 p-3.5">
                    <div className="text-[10px] font-bold tracking-wider text-[#C43A89] uppercase mb-1">Personalised guidance</div>
                    <p className="text-[12.5px] text-slate-700 leading-relaxed">Would you like a 30-minute consultation with our MARA agent for a tailored assessment?</p>
                    <div className="flex gap-2 mt-3">
                      <Link to="/book-consultation" onClick={() => setOpen(false)} data-testid="chat-cta-book" className="bg-[#C43A89] hover:bg-[#a22b6e] text-white text-[11px] font-bold tracking-wider uppercase px-3 py-2 inline-flex items-center gap-1.5">
                        <Calendar className="h-3 w-3" /> Book Consultation
                      </Link>
                      <button onClick={(e) => e.preventDefault()} className="bg-white border border-slate-300 hover:border-[#29324A] text-slate-700 text-[11px] font-bold tracking-wider uppercase px-3 py-2">Continue Chat</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 bg-white">
            {step < 99 ? (
              <div className="p-3 flex items-center justify-between gap-3">
                <button onClick={() => step > 0 ? setStep((s) => s - 1) : setOpen(false)} className="text-[12px] text-slate-500 hover:text-[#29324A] inline-flex items-center gap-1" data-testid="chat-back">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                {step === 5 ? (
                  <button data-testid="chat-submit-onboarding" disabled={submitting} onClick={submitOnboarding} className="bg-gradient-to-br from-[#29324A] to-[#C43A89] hover:from-[#1f2638] hover:to-[#a22b6e] text-white text-[12px] font-bold tracking-wider uppercase px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60">
                    {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</> : <>Start Chat <Send className="h-3.5 w-3.5" /></>}
                  </button>
                ) : step > 0 ? (
                  <button data-testid="chat-next" onClick={next} className="bg-[#29324A] hover:bg-[#1f2638] text-white text-[12px] font-bold tracking-wider uppercase px-5 py-2.5 inline-flex items-center gap-2">Next <ChevronRight className="h-3.5 w-3.5" /></button>
                ) : <span />}
              </div>
            ) : (
              <div className="p-2.5 flex items-end gap-2">
                <textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  rows={1}
                  placeholder="Type your message…"
                  className="flex-1 text-[13px] resize-none outline-none border border-slate-200 focus:border-[#29324A] px-3 py-2 max-h-24"
                />
                <button data-testid="chat-send" onClick={sendMessage} disabled={!input.trim() || sending} className="bg-gradient-to-br from-[#29324A] to-[#C43A89] hover:from-[#1f2638] hover:to-[#a22b6e] text-white p-2.5 disabled:opacity-50">
                  <Send className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="px-3 py-2 bg-[#FAF7FC] border-t border-slate-100 flex items-start gap-2">
              <ShieldAlert className="h-3 w-3 text-[#C43A89] mt-0.5 shrink-0" />
              <p className="text-[10.5px] text-slate-500 leading-snug">
                This tool provides general information only using automated assessment technology — not migration advice. Information may not apply to your individual circumstances. {step === 99 && <button onClick={resetChat} className="text-[#29324A] hover:text-[#C43A89] underline">Start over</button>}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ───────── Small primitives ─────────
const inp = "w-full px-3 py-2 text-[13px] border border-slate-200 focus:border-[#29324A] outline-none bg-white";

function Field({ label, err, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold tracking-wider text-slate-600 uppercase block mb-1.5">{label}</label>
      {children}
      {err && <p className="text-[11px] text-rose-600 mt-1">{err}</p>}
    </div>
  );
}

function FormCard({ children }) {
  return <div className="bg-white border border-slate-200 p-3.5 space-y-3.5">{children}</div>;
}

function BotBubble({ children }) {
  return (
    <div className="flex items-start gap-2">
      <div className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-[#C43A89] to-[#C43A89] flex items-center justify-center mt-0.5">
        <Sparkles className="h-3.5 w-3.5 text-white" />
      </div>
      <div className="max-w-[85%] bg-white border border-slate-200 px-3.5 py-2.5 shadow-sm">{children}</div>
    </div>
  );
}

function Pills({ opts, value, onChange, testIdPrefix = "pill", wrap = false }) {
  return (
    <div className={`flex ${wrap ? "flex-wrap" : "flex-col sm:flex-row"} gap-1.5`}>
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            type="button"
            key={o.v}
            data-testid={`${testIdPrefix}-${String(o.v).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => onChange(o.v)}
            className={`text-[12px] font-medium px-3 py-1.5 border transition-colors text-left ${
              active
                ? "bg-[#29324A] border-[#29324A] text-white"
                : "bg-white border-slate-200 text-slate-700 hover:border-[#29324A] hover:text-[#29324A]"
            }`}
          >
            {o.l}
          </button>
        );
      })}
    </div>
  );
}
